// KPI / stat card: label, value, an accent icon, and an optional trend chip.
// Colors resolve from the theme palette so both themes look correct.

import React from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string|number} props.value
 * @param {React.ElementType} [props.icon]
 * @param {"primary"|"secondary"|"success"|"info"|"warning"|"error"} [props.color]
 * @param {number} [props.trend]  positive/negative percentage; omit to hide
 */
const StatCard = ({ label, value, icon: Icon, color = "primary", trend }) => {
  const up = typeof trend === "number" && trend >= 0;
  return (
    <Card sx={{ p: 2.5, height: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            {value}
          </Typography>
        </Box>
        {Icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: (t) => alpha(t.palette[color].main, 0.14),
              color: `${color}.main`,
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>
        )}
      </Box>
      {typeof trend === "number" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
          {up ? (
            <ArrowUpwardIcon sx={{ fontSize: 16, color: "success.main" }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 16, color: "error.main" }} />
          )}
          <Typography variant="caption" sx={{ color: up ? "success.main" : "error.main", fontWeight: 700 }}>
            {Math.abs(trend)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            vs last period
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default StatCard;
